// src/admin/components/AdminHeader.jsx
import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Bell,
  Menu,
  LogOut,
  ChevronDown,
} from "lucide-react";
import { useAdminAuth } from "../context/AdminAuthContext";
import { useAdminData } from "../context/AdminDataContext";

const routeTitleMap = {
  "/admin/dashboard": "Dashboard",
  "/admin/daily-ca": "Daily Current Affairs",
  "/admin/monthly-ca": "Monthly Current Affairs",
  "/admin/question-bank": "Question Bank",
  "/admin/prelims": "Prelims Initiative",
  "/admin/mains": "Mains Initiative",
  "/admin/results": "Student Results",
};

const AdminHeader = ({ setIsMobileOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { adminUser, logout } = useAdminAuth();
  const { activities } = useAdminData();

  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const notifRef = useRef(null);
  const profileRef = useRef(null);

  const pageTitle = routeTitleMap[location.pathname] || "Admin Dashboard";

  // Close notification & profile dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setIsNotifOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    setIsProfileOpen(false);
    logout();
    navigate("/");
  };

  const displayName = adminUser?.name || "Selva Kumar";
  const displayEmail = adminUser?.email || "admin@gmail.com";
  const userInitial = displayName.charAt(0).toUpperCase();

  return (
    <header className="sticky top-0 z-30 h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6">
      {/* Left: Mobile Toggle & Page Title */}
      <div className="flex items-center gap-3 min-w-0">
        <button
          onClick={() => setIsMobileOpen(true)}
          className="md:hidden p-1.5 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100"
          title="Open Menu"
        >
          <Menu size={20} />
        </button>

        <h1 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight truncate">
          {pageTitle}
        </h1>
      </div>

      {/* Right: Notifications & Profile with Mail ID + Logout */}
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Small Notification Icon */}
        <div ref={notifRef} className="relative">
          <button
            onClick={() => {
              setIsNotifOpen(!isNotifOpen);
              setIsProfileOpen(false);
            }}
            className="p-1.5 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors relative"
            title="Notifications"
          >
            <Bell size={18} />
            <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-red-600" />
          </button>

          {isNotifOpen && (
            <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden z-50">
              <div className="px-3.5 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                <span className="text-xs font-bold text-slate-800">Notifications</span>
                <span className="text-[10px] text-slate-500">Recent</span>
              </div>
              <div className="max-h-60 overflow-y-auto divide-y divide-slate-100">
                {(activities || []).slice(0, 4).map((act) => (
                  <div key={act.id} className="p-3 text-xs hover:bg-slate-50">
                    <p className="font-semibold text-slate-800 line-clamp-1">{act.action}</p>
                    <p className="text-[11px] text-slate-500 line-clamp-1">{act.target}</p>
                    <span className="text-[10px] text-slate-400 mt-1 block">{act.time}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Profile Pill with Mail ID & Dropdown containing Logout option */}
        <div ref={profileRef} className="relative">
          <button
            onClick={() => {
              setIsProfileOpen(!isProfileOpen);
              setIsNotifOpen(false);
            }}
            className="flex items-center gap-2.5 pl-2 sm:pl-3 pr-2 py-1 rounded-xl hover:bg-slate-100/90 transition-colors border-l border-slate-200 group"
            title="Account settings & logout"
          >
            <div className="w-8 h-8 rounded-full bg-red-900 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 shadow-2xs">
              {userInitial}
            </div>
            <div className="text-left hidden sm:block min-w-0 max-w-[170px]">
              <p className="text-xs font-bold text-slate-800 leading-tight truncate">
                {displayName}
              </p>
              <p className="text-[11px] font-medium text-slate-500 leading-tight truncate">
                {displayEmail}
              </p>
            </div>
            <ChevronDown
              size={14}
              className={`text-slate-400 group-hover:text-slate-700 transition-transform duration-150 ${
                isProfileOpen ? "rotate-180 text-red-900" : ""
              }`}
            />
          </button>

          {/* Profile Dropdown Menu */}
          {isProfileOpen && (
            <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50">
              {/* Account Details Header */}
              <div className="p-3.5 bg-slate-50/80 border-b border-slate-100">
                <div className="flex items-center gap-2.5 mb-2">
                  <div className="w-9 h-9 rounded-full bg-red-900 text-white text-sm font-bold flex items-center justify-center flex-shrink-0 shadow-xs">
                    {userInitial}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-slate-900 truncate">
                      {displayName}
                    </p>
                    <p className="text-[11px] text-slate-500 truncate font-mono">
                      {displayEmail}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between text-[10px] pt-1">
                  <span className="font-semibold text-slate-500 uppercase tracking-wider">Access</span>
                  <span className="px-2 py-0.5 rounded-full font-bold bg-amber-100 text-amber-800">
                    Administrator
                  </span>
                </div>
              </div>

              {/* Actions: Logout */}
              <div className="p-1.5">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold text-rose-700 hover:bg-rose-50 hover:text-rose-800 transition-colors text-left"
                >
                  <LogOut size={15} className="text-rose-600 flex-shrink-0" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;
